/* -------------------------------------------------------------------------------------------------
Restricted - Copyright (C) Siemens Healthcare GmbH/Siemens Medical Solutions USA, Inc., 2020. All rights reserved
------------------------------------------------------------------------------------------------- */
import { css, html, LitElement } from 'lit-element';
import { sharedStyles } from '../../styles';
import { dispatchCustomEvent } from '../../utils/custom-event-dispatcher.js';
import { deviceIdentifier } from '../../utils/device-identifier.js';
import { getPatientIllustration } from './patient_illustration.js';

/**
 * @prop {String} image - Default ct_adult_anatomical. Defines the image shown. Possible values are `ct_adult_anatomical`, `ct_adult_vascular`, `ct_child_anatomical` and `ct_child_vascular`.
 * @prop {Number} active - Defines the active area (only one area can be active at a time). Clicking on a region that is not disabled will update the active state.
 * @prop {Array} disabled - Sets the body regions that are disabled and cannot be selected (takes an array of numbers).
 * @prop {String} currentSelected - Defines the selected first level body part. Possible values are: `xp_lower_extremities`, `xp_pelvis`, `xp_abdomen`, `xp_upper_extremities`, `xp_thorax`, `xp_shoulder_girdle`, `xp_vertebral_column` or `xp_skull`. Only for multi Level Selection.
 * @fires active-changed - Dispatched when clicking on a body region.
 * @fires disabled-changed - Dispatched when the disabled array is changed.
 * @fires image-changed - Dispatched when the image is changed.
 */

export class SHPatientIllustration extends LitElement {
  static get properties() {
    return {
      image: { type: String, reflect: true },
      active: { type: Number, reflect: true },
      disabled: { type: Array, reflect: true },
      currentSelected: {
        type: String,
        reflect: true,
        attribute: 'current-selected',
      },
      _illustrationLevel: { type: Number },
      _lastSelected: { type: String },
      _currentActive: { type: String, reflect: true },
      _areaStates: {
        type: JSON,
      },
    };
  }
  static get styles() {
    return [
      sharedStyles,
      css`
        :host {
          height: 100%;
          width: 100%;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
        }

        svg {
          height: 100%;
          width: 100%;
        }

        .region[hover] {
          fill: rgba(var(--ui-1), var(--opacity-5));
        }

        .region {
          fill: rgba(var(--ui-1), var(--opacity-6));
          cursor: pointer;
          transition: 0.2s all ease-in-out;
        }

        .region[disabled] {
          fill: transparent;
          pointer-events: none;
        }

        .region[active] {
          fill: rgba(var(--ui-2), var(--opacity-3));
        }

        .outline {
          fill: rgba(var(--ui-1), var(--opacity-5));
        }

        .vascular {
          fill: rgba(var(--functional-red), 1);
          pointer-events: none;
        }

        .bone {
          fill: rgba(var(--ui-1), var(--opacity-5));
          pointer-events: none;
        }

        .bone[disabled] {
          fill: rgba(var(--ui-1), var(--opacity-7));
          pointer-events: none;
        }

        .transparent {
          fill: transparent;
        }
      `,
    ];
  }
  constructor() {
    super();
    this.image = 'ct_adult_anatomical';
    this._illustrationLevel = 0;
    this._areaStates = {
      xp_skull: {
        hidden: ['xp_skull_selection'],
        disabled: ['xp_skull_bone'],
      },
      xp_upper_extremities: {
        hidden: ['xp_upper_extremities_selection'],
        disabled: ['xp_upper_extremities_bone'],
      },
      xp_thorax: {
        hidden: ['xp_thorax_selection'],
        disabled: ['xp_thorax_bone'],
        active: ['xp_thorax_1'],
      },
      xp_shoulder_girdle: {
        hidden: [
          'xp_shoulder_girdle_selection',
          'xp_shoulder_girdle_bone',
          'xp_shoulder_girdle_default',
          'xp_thorax_bone',
        ],
        disabled: [],
      },
      xp_vertebral_column: {
        hidden: [
          'xp_vertebral_column_default',
          'xp_vertebral_column_bone',
          'xp_vertebral_column_selection',
          'xp_thorax_bone',
          'xp_clavicula_default',
        ],
        disabled: [],
      },
      xp_lower_extremities: {
        hidden: ['xp_lower_extremities_selection'],
        disabled: ['xp_lower_extremities_bone'],
      },
      xp_abdomen: {
        hidden: ['xp_abdomen_selection'],
        disabled: [],
        active: ['xp_abdomen_1'],
      },
      xp_pelvis: {
        hidden: ['xp_pelvis_selection'],
        disabled: ['xp_pelvis_bone'],
      },
    };
  }
  render() {
    return html`${getPatientIllustration(this.image)}`;
  }
  connectedCallback() {
    super.connectedCallback();
    this._clickListener = this._clickHandler.bind(this);
    this._mouseEnterListener = this._mouseEnterHandler.bind(this);
    this._mouseLeaveListener = this._mouseLeaveHandler.bind(this);
    this._xpclickListener = this._xpclickHandler.bind(this);
  }

  firstUpdated() {
    if (deviceIdentifier.isTouchDevice()) {
      this.classList.add('touch-device');
    }
  }
  updated(changedProperties) {
    super.updated(changedProperties);
    if (changedProperties.has('image')) {
      this._handleImageChanged();
      dispatchCustomEvent(this, 'image-changed');
    }
    if (changedProperties.has('active')) {
      this._handleactiveChanged();
      dispatchCustomEvent(this, 'active-changed');
    }
    if (changedProperties.has('currentSelected') && this.image === 'xp_all_in_one') {
      this._resetState();
      this._lastSelected = this.currentSelected;
      this._handlecurrentSelectedChanged();
    }
    if (changedProperties.has('disabled')) {
      this._handledisabledChanged();
      dispatchCustomEvent(this, 'disabled-changed');
    }
  }
  disconnectedCallback() {
    this.removeEventListener('click', this._xpclickListener);
    this.removeEventListener('touchend', this._xpclickListener);
    let regions = this.shadowRoot.querySelectorAll('.region');
    for (let i = 0; i < regions.length; i++) {
      if (this.image !== 'xp_all_in_one') {
        regions[i].removeEventListener('click', this._clickListener);
      }
      if (!deviceIdentifier.isTouchDevice()) {
        regions[i].removeEventListener('mouseenter', this._mouseEnterListener);
        regions[i].removeEventListener('mouseleave', this._mouseLeaveListener);
      }
    }
    super.disconnectedCallback();
  }
  _handleImageChanged() {
    let svg = this.shadowRoot.querySelector('svg');
    svg.removeAttribute('hidden');
    let regions = this.shadowRoot.querySelectorAll('.region');
    for (let i = 0; i < regions.length; i++) {
      if (this.image !== 'xp_all_in_one') {
        regions[i].addEventListener('click', this._clickListener);
      }
      if (!deviceIdentifier.isTouchDevice()) {
        regions[i].addEventListener('mouseenter', this._mouseEnterListener);
        regions[i].addEventListener('mouseleave', this._mouseLeaveListener);
      }
    }
    this.addEventListener('click', this._xpclickListener);
    this._handleactiveChanged();
    this._handledisabledChanged();
  }
  _mouseEnterHandler(event) {
    let regions = this.shadowRoot.querySelectorAll('.region');
    for (let j = 0; j < regions.length; j++) {
      if (regions[j].getAttribute('index') == event.target.getAttribute('index')) {
        regions[j].setAttribute('hover', true);
      }
    }
  }
  _mouseLeaveHandler(event) {
    let regions = this.shadowRoot.querySelectorAll('.region');
    for (let j = 0; j < regions.length; j++) {
      if (regions[j].getAttribute('index') == event.target.getAttribute('index')) {
        regions[j].removeAttribute('hover');
      }
    }
  }
  _handledisabledChanged() {
    if (this.disabled) {
      let regions = this.shadowRoot.querySelectorAll('.region');
      for (let i = 0; i < regions.length; i++) {
        if (this.disabled.indexOf(regions[i].getAttribute('index')) >= 0) {
          regions[i].setAttribute('disabled', true);
        } else {
          regions[i].removeAttribute('disabled');
        }
      }
    }
  }
  _handleactiveChanged() {
    if (this.image !== 'xp_all_in_one') {
      let regions;
      regions = this.shadowRoot.querySelectorAll('.region');
      for (let i = 0; i < regions.length; i++) {
        if (regions[i].getAttribute('index') == this.active) {
          regions[i].setAttribute('active', true);
        } else {
          regions[i].removeAttribute('active');
        }
      }
    }
  }
  _handlecurrentSelectedChanged() {
    if (this.image !== 'xp_all_in_one') {
      return;
    }
    this.shadowRoot
      .querySelectorAll('.region')
      .forEach((region) => region.removeAttribute('active'));
    this._illustrationLevel = 0;
    if (this.currentSelected) {
      this._illustrationLevel = 1;
      //get state for last seledted in JSON
      let json = this._areaStates[this.currentSelected];
      this._setStates(json, false);
      this.shadowRoot.querySelector('#default').style.opacity = '0';
    }
  }
  _clickHandler(e) {
    e.preventDefault();
    if (this.active != e.target.getAttribute('index')) {
      this.active = e.target.getAttribute('index');
    } else {
      this.active = undefined;
    }
  }
  _xpclickHandler(event) {
    event.preventDefault();
    //changes done for firefox console error
    let path = event.path || (event.composedPath && event.composedPath());
    if (path[0].id === 'outline') {
      return;
    } else if (path[1].id === 'back-to-default') {
      this._backToDefault();
    } else if (this._illustrationLevel === 1) {
      //if second element is currentSelected + "_selection"
      if (path[1].id === this.currentSelected + '_selection') {
        this.shadowRoot.querySelector('#' + path[0].id).setAttribute('active', true);
        this._currentActive = path[0].id;
        this._illustrationLevel = 2;
        //-> jump to second level
      } else if (path[2].id === this.currentSelected + '_selection') {
        this.shadowRoot.querySelector('#' + path[1].id).setAttribute('active', true);
        this._currentActive = path[1].id;
        this._illustrationLevel = 2;
        //-> jump to second level
      } else if (path[0].tagName === 'path' && path[0].id === '') {
        this._selectArea(path, 1);
      } else if (path[0].tagName === 'path' && path[0].id !== '') {
        this._selectArea(path, 0);
      }
    } else if (this._illustrationLevel === 2) {
      //same level 1 but different level 2
      if (
        this.currentSelected + '_selection' === path[1].id &&
        this._currentActive !== path[0].id
      ) {
        //one path
        this.shadowRoot.querySelector('#' + this._currentActive).removeAttribute('active');
        this.shadowRoot.querySelector('#' + path[0].id).setAttribute('active', true);
        this._currentActive = path[0].id;
        this._illustrationLevel = 2;
      } else if (
        this.currentSelected + '_selection' === path[2].id &&
        this._currentActive !== path[1].id
      ) {
        //group with several path
        this.shadowRoot.querySelector('#' + this._currentActive).removeAttribute('active');
        this.shadowRoot.querySelector('#' + path[1].id).setAttribute('active', true);
        this._currentActive = path[1].id;
        this._illustrationLevel = 2;
      } else if (this._currentActive === path[0].id || this._currentActive === path[1].id) {
        //_currentActive clicked again
        return;
      } else {
        //different level 1 click
        this._illustrationLevel = 1;
        this._lastSelected = this.currentSelected;
        this._illustrationLevel = 0;
        this.currentSelected = '';
      }
    }
    if (
      this._illustrationLevel === 0 &&
      path[0].tagName === 'path' &&
      path[1].id !== 'back-to-default'
    ) {
      if (path[0].id === '') {
        this._selectArea(path, 1);
      } else {
        this._selectArea(path, 0);
      }
    }
  }
  _backToDefault() {
    this._lastSelected = this.currentSelected;
    this._illustrationLevel = 0;
    this.currentSelected = '';
  }
  _resetState() {
    if (this._lastSelected == undefined) {
      return;
    }
    //check if it is a jump from level 0
    if (this._lastSelected) {
      //get state for last seledted in JSON
      let json = this._areaStates[this._lastSelected];
      this._setStates(json, true);

      if (this._currentActive && this._illustrationLevel < 2) {
        this.shadowRoot.querySelector('#' + this._currentActive).removeAttribute('active');
        this._currentActive = '';
      }
    }
    if (this._illustrationLevel === 0) {
      this.shadowRoot.querySelector('#default').style.opacity = '1';
    }
  }
  _setStates(json, reset) {
    let toHide = json['hidden'];
    let toDisable = json['disabled'];
    let toActive = json['active'];
    //reset all related changes
    if (toHide) {
      toHide.forEach((element) => {
        this._setState(element, 'hidden');
      });
    }
    if (toDisable) {
      toDisable.forEach((element) => {
        this._setState(element, 'disabled');
      });
    }
    if (toActive) {
      toActive.forEach((element) => {
        this._setState(element, 'active');
        if (!reset) this._currentActive = element;
      });
      if (!reset) this._illustrationLevel = 2;
    }
  }
  _selectArea(item, position) {
    if (this._illustrationLevel === 0) {
      this.currentSelected = item[position].id;
    } else if (this._illustrationLevel === 1) {
      this._lastSelected = this.currentSelected;
      this.currentSelected = item[position].id;
    }
  }
  _setState(id, attribute) {
    let path = this.shadowRoot.querySelector('#' + id);
    if (path.hasAttribute(attribute)) {
      path.removeAttribute(attribute);
    } else {
      path.setAttribute(attribute, true);
    }
  }
}

customElements.define('sh-patient-illustration', SHPatientIllustration);
